/**
 * Shared logic for resolving the SPA route a notification click should open.
 *
 * Catalog deep links arrive in several shapes:
 *   - absolute apex URLs   `https://app.nibni.com/offer/<id>`   (current catalog shape)
 *   - neutral SPA routes   `/n/offer/<id>`                      (legacy)
 *   - hash forms           `#/n/offer/<id>`, `/app/#/...`       (older app builds)
 *
 * `normalizeDeepLinkPath` folds all of them into an in-SPA path the
 * NotificationRedirectComponent (mounted at /n/:kind[/:id]) can resolve —
 * the same rules PushNotificationsService.handleDeepLink has always applied.
 *
 * On top of that, some notification types must open a WORKFLOW page rather
 * than the linked entity. The deep link alone cannot express that (it is a
 * shared apex URL, kept stable for the mobile App Links), so the override is
 * keyed off the stable `type` column — which also covers rows stored before
 * the override existed:
 *   - offer_approval_pending → the offer approvals page (pending queue)
 *   - meta_lead_sync_digest  → the Lead Management list (one digest per 5-min
 *     Meta sync run replaces per-lead notifications)
 */
export const NOTIFICATION_TYPE_ROUTE_OVERRIDES: Record<string, string> = {
    offer_approval_pending: '/developer/offers/approvals',
    meta_lead_sync_digest: '/developer/leads',
    // Hold-extension workflow: every leg lands on the lead/reservation surface
    // where the pending request and its decisions are shown.
    reservation_hold_extension_pending: '/developer/reservations',
    reservation_hold_extension_approved: '/developer/reservations',
    reservation_hold_extension_rejected: '/developer/reservations',
    reservation_hold_extension_expired: '/developer/reservations'
};

const ID_KINDS = ['property', 'project', 'offer', 'reservation', 'lead', 'document'];
const STATIC_KINDS = ['team', 'partners', 'invitations', 'kyc', 'announcements', 'test'];
const ALL_KINDS = [...ID_KINDS, ...STATIC_KINDS];

export function normalizeDeepLinkPath(deepLink: string): string {
    let target = String(deepLink || '').trim();
    if (!target) return '/';
    try {
        const u = new URL(target, window.location.origin);
        // Prefer the hash fragment (legacy hash-routed shapes encode the SPA
        // route there): #/n/offer/123 → /n/offer/123
        if (u.hash && u.hash.startsWith('#/')) {
            target = u.hash.slice(1);
        } else if (u.pathname) {
            // No usable hash — apex path form (the live catalog shape).
            let p = u.pathname;
            if (p.startsWith('/app/')) p = p.slice('/app'.length);
            else if (p === '/app') p = '/';
            if (p.startsWith('/n/')) p = p.slice('/n'.length);
            const segments = p.replace(/^\/+/, '').split('/');
            const kind = segments[0] || '';
            if (ALL_KINDS.includes(kind)) {
                const id = segments.length > 1 ? '/' + segments.slice(1).join('/') : '';
                p = `/n/${kind}${id}`;
            }
            target = p || '/';
        } else {
            target = '/';
        }
    } catch {
        // Not an absolute URL — relative/#-prefixed forms handled below.
    }
    if (target.startsWith('#')) {
        target = target.slice(1);
    } else if (!target.startsWith('/')) {
        target = '/' + target;
    }
    return target || '/';
}

/** A lead ID is a UUID in the existing API. Link data never grants access. */
export function leadCreatedTarget(deepLink: string | null | undefined, data?: Record<string, unknown> | null): string {
    const uuid = /^[0-9a-f]{8}-[0-9a-f]{4}-[0-9a-f]{4}-[0-9a-f]{4}-[0-9a-f]{12}$/i;
    const validId = (value: unknown): string | null =>
        typeof value === 'string' && uuid.test(value.trim()) ? value.trim().toLowerCase() : null;
    const dataId = validId(data?.['lead_id']);
    let linkId: string | null = null;
    try {
        const url = new URL(String(deepLink || '').trim(), 'https://notification.invalid');
        const path = (url.hash.startsWith('#/') ? url.hash.slice(1).split('?')[0] : url.pathname)
            .replace(/^\/app(?=\/|$)/, '');
        const match = path.match(/^\/(?:n\/lead|lead|developer\/leads|subpartner\/leads|sales\/leads)\/([^/]+)\/?$/);
        linkId = match ? validId(decodeURIComponent(match[1])) : null;
    } catch { /* Invalid/encoded paths may still have a trustworthy payload ID. */ }
    // Conflicting valid IDs are ambiguous; never guess which profile was meant.
    if (linkId && dataId && linkId !== dataId) return '/developer/leads';
    const id = linkId || dataId;
    return id ? `/n/lead/${encodeURIComponent(id)}` : '/developer/leads';
}

/**
 * Resolves the SPA path for a notification click: the type override when the
 * type has one, otherwise the normalized deep link. Returns null when there
 * is nothing to navigate to.
 */
export function notificationClickTarget(input: { type?: string | null; deepLink?: string | null; data?: Record<string, unknown> | null }): string | null {
    if (String(input?.type || '').trim() === 'lead_created') return leadCreatedTarget(input.deepLink, input.data);
    const override = NOTIFICATION_TYPE_ROUTE_OVERRIDES[String(input?.type || '').trim()];
    if (override) return override;
    const deepLink = String(input?.deepLink || '').trim();
    return deepLink ? normalizeDeepLinkPath(deepLink) : null;
}
